<template>
    <ul class="navigation__menu" role="menu">
        <template v-for="(navItem, index) in menu" :key="navItem._uid">
            <li
                v-if="navItem.component === 'Dropdown' && navItem.ShowOnLogin && isLoggedIn"
                :class="{
                    navigation__dropdown: navItem.Options.length,
                    'current-page-nav-item': currentPageMap[navItem._uid],
                }"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
            >
                <DropdownItem
                    :nav-item="navItem as IMenuDropdown"
                    :menu-level="2"
                    :data-type="getDataType(navItem)"
                    :data-cy="`menuDropdown-${navItem._uid}`"
                    :opened-submenu-id="openedSubmenu"
                    @active-submenu="submenuHandle"
                    @logout="logout"
                />
            </li>
            <li
                v-else-if="navItem.component === 'LinkHtmlContent' && navItem.Text?.type === 'doc' && navItem?.Link.url"
                :class="[
                    { 'current-page-nav-item': currentPageMap[navItem._uid] },
                    'navigation__html-item',
                    'rich-text-menu-item',
                ]"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
                :data-type="getDataType(navItem)"
            >
                <nuxt-link
                    :to="getInternalLink(navItem?.Link)"
                    external
                    :style="`
                        ${navItem.background?.color ? `background-color: ${navItem.background?.color};` : ''}
                        ${navItem.color?.color ? `color: ${navItem.color?.color};` : ''}
                    `"
                    :data-cy="`menuLinkHtmlContent-${index}`"
                    :data-gtm-id="stringId('header_link', navItem.trackingId)"
                    @click.prevent.stop="openModal(navItem)"
                >
                    <SimpleRichtext :blok="{ text: navItem.Text }" />
                </nuxt-link>
            </li>
            <li
                v-else-if="navItem.component === 'LinkHtmlContent' && navItem.ModalId && !isLoggedIn"
                :class="[
                    { 'current-page-nav-item': currentPageMap[navItem._uid] },
                    'navigation__html-item',
                    'rich-text-menu-item',
                ]"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
            >
                <button
                    type="button"
                    aria-haspopup="dialog"
                    :aria-controls="navItem.ModalId"
                    :style="`
                        ${navItem.background?.color ? `background-color: ${navItem.background?.color};` : ''}
                        ${navItem.color?.color ? `color: ${navItem.color?.color};` : ''}
                    `"
                    @click.prevent.stop="openModal(navItem)"
                    :data-cy="`menuModalHtmlContent-${index}`"
                >
                    <SimpleRichtext :blok="{ text: navItem.Text }" />
                </button>
            </li>
            <li
                v-else-if="
                    (navItem.component === 'Dropdown' && !navItem.ShowOnLogin && !isLoggedIn) ||
                    (navItem.component === 'Dropdown' && !navItem.ShowOnLogin && !navItem?.Link.url)
                "
                :class="{
                    navigation__dropdown: navItem.Options.length,
                    'current-page-nav-item': currentPageMap[navItem._uid],
                }"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
            >
                <DropdownItem
                    :nav-item="navItem"
                    :menu-level="2"
                    :data-type="getDataType(navItem)"
                    :data-cy="`menuDropdown-${navItem._uid}`"
                    :opened-submenu-id="openedSubmenu"
                    @active-submenu="submenuHandle"
                    @logout="logout"
                />
            </li>
            <li
                v-else-if="navItem.component === 'NavHtmlItem'"
                class="navigation__html-item"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
            >
                <HtmlContent :nav-item="navItem" :data-cy="`menuHTMLItem-${index}`" />
            </li>
            <li
                v-else-if="navItem.component === 'Link_Icon' && navItem?.ModalId && !navItem.ShowOnLogin && !isLoggedIn"
                :class="[{ 'current-page-nav-item': currentPageMap[navItem._uid] }, 'navigation__link-icon']"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
            >
                <button
                    type="button"
                    aria-haspopup="dialog"
                    :aria-controls="navItem.ModalId"
                    :data-cy="`menuLinkIconModal-${index}`"
                    @click.prevent.stop="openModal(navItem)"
                >
                    <template v-if="typeof navItem.Icon === 'string' && navItem.Icon && !navItem.CustomIcon?.filename">
                        <NuxtIcon :name="getNormalizedIconifyIcon(navItem.Icon, '')" />
                    </template>
                    <NuxtImg
                        v-if="navItem.CustomIcon?.filename"
                        :src="navItem.CustomIcon.filename"
                        :alt="navItem.CustomIcon.filename"
                        format="webp"
                        loading="eager"
                        height="16"
                        width="16"
                    />
                    {{ navItem.Text }}
                </button>
            </li>
            <li
                v-else-if="
                    navItem.component === 'Link_Icon' && navItem.Link?.url && !navItem.ModalId && !navItem.ShowOnLogin
                "
                :class="[{ 'current-page-nav-item': currentPageMap[navItem._uid] }, 'navigation__link-icon']"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
                :data-type="getDataType(navItem)"
            >
                <nuxt-link
                    :to="getInternalLink(navItem.Link)"
                    :target="navItem.Target"
                    :data-cy="`menuLinkIconLink-${index}`"
                    :data-gtm-id="stringId('header_link', navItem.trackingId, navItem.Text)"
                    external
                >
                    <template v-if="typeof navItem.Icon === 'string' && navItem.Icon && !navItem.CustomIcon?.filename">
                        <NuxtIcon :name="getNormalizedIconifyIcon(navItem.Icon, '')" />
                    </template>
                    <NuxtImg
                        v-if="navItem.CustomIcon?.filename"
                        :src="navItem.CustomIcon.filename"
                        :alt="navItem.CustomIcon.filename"
                        format="webp"
                        loading="eager"
                        height="16"
                        width="16"
                    />
                    {{ navItem.Text }}
                </nuxt-link>
            </li>
            <li
                v-else-if="navItem.component === 'Link_Icon' && !navItem.ModalId && navItem.ShowOnLogin && isLoggedIn"
                :class="[{ 'current-page-nav-item': currentPageMap[navItem._uid] }, 'navigation__link-icon']"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
                :data-type="getDataType(navItem)"
            >
                <a
                    v-if="(navItem.Link?.url || navItem.Link?.cached_url) === '/logout'"
                    :target="navItem.Target"
                    :href="getInternalLink(navItem.Link)"
                    :data-cy="`menuLinkIconLogout-${index}`"
                    :data-gtm-id="stringId('header_link', navItem.trackingId, navItem.Text)"
                    external
                    @click.prevent.stop="logout()"
                >
                    <template v-if="typeof navItem.Icon === 'string' && navItem.Icon && !navItem.CustomIcon?.filename">
                        <NuxtIcon :name="getNormalizedIconifyIcon(navItem.Icon, '')" />
                    </template>
                    <NuxtImg
                        v-if="navItem.CustomIcon?.filename"
                        :src="navItem.CustomIcon.filename"
                        :alt="navItem.CustomIcon.filename"
                        format="webp"
                        loading="eager"
                        height="16"
                        width="16"
                    />
                    {{ navItem.Text }}
                </a>
                <nuxt-link
                    v-else
                    :to="getInternalLink(navItem.Link)"
                    :target="navItem.Target"
                    :data-cy="`menuLinkIconLink-${index}`"
                    :data-gtm-id="stringId('header_link', navItem.trackingId, navItem.Text)"
                    external
                >
                    <template v-if="typeof navItem.Icon === 'string' && navItem.Icon && !navItem.CustomIcon?.filename">
                        <NuxtIcon :name="getNormalizedIconifyIcon(navItem.Icon, '')" />
                    </template>
                    <NuxtImg
                        v-if="navItem.CustomIcon?.filename"
                        :src="navItem.CustomIcon.filename"
                        :alt="navItem.CustomIcon.filename"
                        format="webp"
                        loading="eager"
                        height="16"
                        width="16"
                    />
                    {{ navItem.Text }}
                </nuxt-link>
            </li>
            <li
                v-else-if="navItem.component === 'Link' && navItem.Link?.url && !navItem.ModalId"
                :class="[{ 'current-page-nav-item': currentPageMap[navItem._uid] }, 'navigation__link']"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
                :data-type="getDataType(navItem)"
            >
                <nuxt-link
                    :to="getInternalLink(navItem.Link)"
                    :target="navItem.Target"
                    :data-cy="`menuLink-${index}`"
                    :data-gtm-id="stringId('header_link', navItem.trackingId, navItem.Text)"
                    external
                >
                    <NuxtImg
                        v-if="navItem.Icon?.filename"
                        :src="navItem.Icon.filename"
                        :alt="navItem.Icon.filename"
                        format="webp"
                        loading="eager"
                        height="16"
                        width="16"
                    />
                    {{ navItem.Text }}
                </nuxt-link>
            </li>
            <li
                v-else-if="navItem.component === 'Link' && navItem?.ModalId && !isLoggedIn"
                class="navigation__link"
                role="menuitem"
                :data-cy="`menuItem-${index}`"
            >
                <button
                    type="button"
                    aria-haspopup="dialog"
                    :aria-controls="navItem.ModalId"
                    :data-cy="`menuLinkModal-${index}`"
                    @click.prevent.stop="openModal(navItem)"
                >
                    <NuxtImg
                        v-if="navItem.Icon?.filename"
                        :src="navItem.Icon.filename"
                        :alt="navItem.Icon.filename"
                        format="webp"
                        loading="eager"
                        height="16"
                        width="16"
                    />
                    {{ navItem.Text }}
                </button>
            </li>
            <li
                v-else-if="navItem.component === 'Separator'"
                class="navigation__separator"
                role="separator"
                :data-cy="`menuItem-${index}`"
            >
                <hr
                    :style="navItem.Color?.color ? `border-bottom: 1px solid ${navItem.Color.color}` : null"
                    :data-cy="`menuSeparator-${index} `"
                />
            </li>
        </template>
    </ul>
</template>

<script setup lang="ts">
import { getInternalLink } from '@nuxt-monorepo/base/app/utils/internalLinking';
import { useModalStore } from '@nuxt-monorepo/base/app/store/modal';
import { useAuthStore } from '@nuxt-monorepo/base/app/store/auth';
import { getNormalizedIconifyIcon, stringId } from '@nuxt-monorepo/ui/utils/helpers';
import type {
    IHeaderConfig,
    IMenuDropdown,
    IMenuLinkIcon,
    ILinkHtmlContent,
    ISeparator,
    IMenuIcon,
    INavHtmlItem,
} from '@nuxt-monorepo/ui/types/store/global';
import type { ILinkItem } from '@nuxt-monorepo/ui/types/components/CoreLinkList';
import DropdownItem from '@nuxt-monorepo/ui/components/Core/Navigation/DropdownItem.vue';
import HtmlContent from '@nuxt-monorepo/ui/components/Header/Components/NavItem/HtmlContent.vue';

const emit = defineEmits(['logout', 'closeMenu']);

const modalStore = useModalStore();
const authStore = useAuthStore();
const route = useRoute();
const { menu, paths } = defineProps<{
    menu: IHeaderConfig['MobileNavigation'] | IHeaderConfig['Navigation'];
    paths?: Record<string, Record<string, string>>; //prop for checking product type on L4
}>();

const isLoggedIn = computed(() => {
    return !!(authStore.$state.id && authStore.$state.role && authStore.$state.csrf);
});

function relativeLink(link: string) {
    if (!link) return;
    const fullUrl = new URL(link, useRequestURL().origin);
    return fullUrl.pathname;
}
const isCurrentPageNavItem = (
    navItem: Exclude<IHeaderConfig['MobileNavigation'][0], ISeparator | IMenuIcon | INavHtmlItem>
) => relativeLink(navItem?.Link?.url || navItem?.Link?.cached_url) === route.path;

// This Map required to make current page calculation static and avoid recalculation on any menu interaction
const currentPageMap = computed(() => Object.fromEntries(menu.map((item) => [item._uid, isCurrentPageNavItem(item)])));

const openModal = (item: IMenuLinkIcon | ILinkHtmlContent | ILinkItem) => {
    if (!item.ModalId) return;
    modalStore.toggleModal(item.ModalId, true);
    emit('closeMenu');
    submenuHandle({ active: true, direction: 'left', openedSubmenu: item._uid });
};

const logout = () => {
    emit('logout');
};

const openedSubmenu = ref<string | null>(null);

const submenuHandle = (state: { active: boolean; direction: string; openedSubmenu: string | null }) => {
    openedSubmenu.value = state.openedSubmenu;
};

const getDataType = (link: IMenuDropdown | ILinkHtmlContent | IMenuLinkIcon | ILinkItem) => {
    // Function to check if the given string is included in either cached_url or url properties of the link object
    const matchUrl = (str: string) => link.Link?.cached_url?.includes(str) || link.Link?.url?.includes(str);

    // Default product type if no matches are found
    let productType = 'defaultLink';

    if (!paths) return productType;
    // Filter through the URL types of the paths object to find matches in localized slugs
    const matchedTypes = Object.keys(paths).filter((urlType) => {
        // Get an array of all slug values for the current URL type using Object.values
        const localizedSlugs = Object.values(paths[urlType]).filter((slug: string) => slug.length);
        // Check if any of the slugs match the URL
        return localizedSlugs.some((slug: string) => matchUrl(slug));
    });

    // Determine the product type based on the matched key, using a switch statement
    switch (true) {
        case matchedTypes.includes('transfers'):
            productType = 'transfers';
            break;
        case matchedTypes.includes('parking'):
            productType = 'airportparking';
            break;
        case matchedTypes.includes('hotel-with-parking'):
            productType = 'hotel';
            break;
        case matchedTypes.includes('train-station-parking'):
            productType = 'train';
            break;
        case matchedTypes.includes('port-parking'):
            productType = 'portparking';
            break;
    }

    // Return the determined product type
    return productType;
};
</script>

<style scoped lang="postcss">
.navigation {
    &__menu {
        @apply max-h-full flex flex-col gap-5 overflow-y-auto overflow-x-hidden;
        .md-mobile & {
            @apply md:flex-row md:w-full md:gap-6 md:h-full md:items-center md:overflow-visible;
        }
        .tablet-mobile & {
            @apply tablet:flex-row tablet:w-full tablet:gap-6 tablet:h-full tablet:items-center tablet:overflow-visible;
        }
        .lg-mobile & {
            @apply lg:flex-row lg:w-full lg:gap-6 lg:h-full lg:items-center lg:overflow-visible;
        }
        .xl-mobile & {
            @apply xl:flex-row xl:w-full xl:gap-6 xl:h-full xl:items-center xl:overflow-visible;
        }
        li {
            @apply border-none text-sm;
            .md-mobile & {
                @apply md:h-full md:text-base;
            }
            .tablet-mobile & {
                @apply tablet:h-full tablet:text-base;
            }
            .lg-mobile & {
                @apply lg:h-full lg:text-base;
            }
            .xl-mobile & {
                @apply xl:h-full xl:text-base;
            }

            div .iconify {
                @apply text-[#293f4d];
            }

            a,
            span {
                @apply py-0 cursor-pointer;

                &:hover {
                    @apply underline;
                }
            }

            a,
            button {
                @apply flex items-center w-full gap-2 font-medium;
                .md-mobile & {
                    @apply md:h-full md:text-base md:hover:text-primary md:transition-colors md:p-2;
                }
                .tablet-mobile & {
                    @apply tablet:h-full tablet:text-base tablet:hover:text-primary tablet:transition-colors tablet:p-2;
                }
                .lg-mobile & {
                    @apply lg:h-full lg:text-base lg:hover:text-primary lg:transition-colors lg:p-2;
                }
                .xl-mobile & {
                    @apply xl:h-full xl:text-base xl:hover:text-primary xl:transition-colors xl:p-2;
                }
                &:focus-visible {
                    @apply outline outline-2 -outline-offset-2;
                }
            }

            .i-ri\:arrow-right-s-line {
                @apply text-[20px];
            }
            &.current-page-nav-item > a {
                @apply text-secondary;
            }
        }

        .rich-text-menu-item {
            @apply h-auto;

            a,
            button {
                @apply block pt-2.5 pb-[5px] px-[15px] h-auto;
                :deep(p) {
                    @apply m-0 text-inherit;
                }
            }
            :deep(.simple-richtext) {
                p {
                    @apply text-base;
                }
                span {
                    @apply text-center w-full block leading-[15px];

                    &.text {
                        @apply text-xs leading-[15px] block mt-0 mb-[3px] mx-0;
                    }

                    &.title {
                        @apply text-[25px] leading-[27px] uppercase font-bold;
                    }
                }
            }
            a {
                @apply block xl:inline-flex pt-2.5 pb-2.5 px-[15px] h-full;
                span {
                    @apply text-center w-full block text-xs leading-[15px];

                    &.text {
                        @apply text-xs leading-[15px] block mt-0 mb-[3px] mx-0;
                    }

                    &.title {
                        @apply text-[25px] leading-[27px] uppercase font-bold;
                    }
                }
            }
        }
    }

    &__html-item {
        @apply items-center;
        .md-mobile & {
            @apply md:flex md:flex-shrink-0;
        }
        .tablet-mobile & {
            @apply tablet:flex md:flex-shrink-0;
        }
        .lg-mobile & {
            @apply lg:flex md:flex-shrink-0;
        }
        .xl-mobile & {
            @apply xl:flex md:flex-shrink-0;
        }
    }

    &__separator {
        .md-mobile & {
            @apply md:ml-auto;
        }
        .tablet-mobile & {
            @apply tablet:ml-auto;
        }
        .lg-mobile & {
            @apply lg:ml-auto;
        }
        .xl-mobile & {
            @apply xl:ml-auto;
        }
    }
}
</style>
